import styles from "./styles/SchoolsDropdown.module.scss";
import FontAwesome from "../utilities/FontAwesome";
import { useEffect, useId, useRef, useState } from "react";
import ClickableLink from "../utilities/ClickableLink";
import classNames from "classnames";
import { CSSTransition } from "react-transition-group";
import { Arrow } from "../utilities/Popup";
import useClickOutside from "../../hooks/useClickOutside";
import { useStores } from "../../stores/useStores";
import { TranslatedText } from "../utilities/TranslatedText";

const transitionClassNames = {
  enter: styles.transitionEnter,
  enterActive: styles.transitionEnterActive,
  enterDone: styles.transitionEnterDone,
  exit: styles.transitionExit,
  exitActive: styles.transitionExitActive,
  exitDone: styles.transitionExitDone,
};

function Dropdown(props: {
  groupedLinks: {
    [k: string]: SchoolLink[];
  };
  open: boolean;
  id: string;
  onEntered: () => void;
}) {
  const { interfaceStore } = useStores();
  const transitionRef = useRef(null);

  return (
    <CSSTransition
      classNames={transitionClassNames}
      in={props.open}
      timeout={250}
      mountOnEnter={true}
      unmountOnExit={true}
      nodeRef={transitionRef}
      onEntered={props.onEntered}
    >
      <div className={styles.dropdown} ref={transitionRef}>
        {interfaceStore.breakpoint === "break-point-xs" && (
          <div className={styles.breakContainer}>
            <hr />
          </div>
        )}
        <ul id={props.id}>
          {props.groupedLinks["District"] && (
            <li key={"District"} className={styles.categoryList}>
              <h2>{"District"}</h2>
              <ul>
                {props.groupedLinks["District"].map((link) => (
                  <li key={link.title}>
                    <ClickableLink href={link.url}>{link.title}</ClickableLink>
                  </li>
                ))}
              </ul>
            </li>
          )}
          {Object.entries(props.groupedLinks || {}).map(
            ([category, links]: [string, SchoolLink[]]) =>
              category !== "District" && (
                <li key={category} className={styles.categoryList}>
                  <h2>{category}</h2>
                  <ul>
                    {links.map((link) => (
                      <li key={link.title}>
                        <ClickableLink href={link.url}>
                          {link.title}
                        </ClickableLink>
                      </li>
                    ))}
                  </ul>
                </li>
              ),
          )}
        </ul>
      </div>
    </CSSTransition>
  );
}

export function SchoolsDropdown(props: { heroBlock: IHeroTerraceBlock }) {
  const { organizationStore } = useStores();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownId = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const ref = useClickOutside(() =>
    setDropdownOpen(false),
  ) as React.MutableRefObject<HTMLDivElement | null>;

  const arrowClassName = classNames({
    [styles.arrow]: true,
    [styles.arrowVisible]: dropdownOpen,
  });

  const containerClassName = classNames({
    [styles.container]: true,
    // should be hidden on sub-pages and when there's no content
    [styles.containerHidden]:
      props.heroBlock.school_links.length === 0 ||
      organizationStore.currentOrganization.id !==
        organizationStore.organization.id,
  });

  const buttonClassName = classNames({
    [styles.stickyButton]: dropdownOpen,
  });

  useEffect(() => {
    function handleKeyPress(e) {
      if (
        dropdownOpen &&
        ((e.shiftKey && e.keyCode === 9) || e.key === "Escape")
      ) {
        e.preventDefault();
        setDropdownOpen(false);
        triggerRef.current?.focus();
      }
    }

    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [dropdownOpen]);

  function focusFirstLink() {
    triggerRef.current?.parentElement
      ?.querySelector<HTMLAnchorElement>("a")
      ?.focus({ preventScroll: true });
  }

  const groupedLinks = props.heroBlock.school_links?.reduce((grouped, link) => {
    (grouped[link.category] = grouped[link.category] || []).push(link);
    return grouped;
  }, {});

  return (
    <div className={containerClassName} ref={ref}>
      <div className={styles.desktopDropdown}>
        <Dropdown
          groupedLinks={groupedLinks}
          open={dropdownOpen}
          id={`${dropdownId}-desktop`}
          onEntered={focusFirstLink}
        />
        <button
          onClick={(e) => {
            triggerRef.current = e.currentTarget;
            setDropdownOpen((v) => !v);
          }}
          aria-controls={`${dropdownId}-desktop`}
          aria-expanded={dropdownOpen}
        >
          <TranslatedText>Our Schools</TranslatedText>&nbsp;
          <FontAwesome prefix={"fas"} name={"fa-arrow-up"} />
        </button>
        <Arrow position={"bottom"} className={arrowClassName} />
      </div>
      <div className={styles.mobileDropdown}>
        <button
          className={buttonClassName}
          onClick={(e) => {
            triggerRef.current = e.currentTarget;
            setDropdownOpen((v) => {
              if (ref.current) {
                ref.current.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }
              return !v;
            });
          }}
          aria-controls={`${dropdownId}-mobile`}
          aria-expanded={dropdownOpen}
        >
          <div className={styles.buttonTextContainer}>
            <TranslatedText>Our Schools</TranslatedText>&nbsp;
            <FontAwesome
              className={styles.faIcon}
              prefix={"fas"}
              name={dropdownOpen ? "fa-angle-down" : "fa-angle-right"}
            />
          </div>
        </button>
        <Dropdown
          groupedLinks={groupedLinks}
          open={dropdownOpen}
          id={`${dropdownId}-mobile`}
          onEntered={focusFirstLink}
        />
      </div>
    </div>
  );
}
